import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"

import tokens from "../_tokens"
import { Button, Icon, Link, NavItem } from "./"
import preval from "preval.macro"
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd"

// This code needs to run at build-time so it can access the file system.
const repositoryUrl = preval`
  const readPkgUp = require('read-pkg-up')
  const getPkgRepo = require('get-pkg-repo')
  try {
    const repo = getPkgRepo(readPkgUp.sync().package)
    module.exports = \`https://github.com/\${repo.user}/\${repo.project}\`
  } catch (error) {
    module.exports = ''
  }
`

const StyledRepoUrlContainer = styled.div`
  border: 0;
  border-top: ${0.5 * tokens.size.base}px;
  padding: ${2.5 * tokens.size.base}px;
`
const StyledNavContainer = styled.div`
  background-color: ${props =>
    props.isDraggingOver
      ? `${tokens.color.primaryTransparent}`
      : "transparent"};
`

const NavContainer = ({ children }) => (
  <Droppable droppableId="droppable" type="droppableItem">
    {(provided, snapshot) => (
      <StyledNavContainer
        ref={provided.innerRef}
        isDraggingOver={snapshot.isDraggingOver}
      >
        {children}
        {provided.placeholder}
      </StyledNavContainer>
    )}
  </Droppable>
)

NavContainer.propTypes = {
  children: PropTypes.node,
}

const StyledNavSection = styled.ul`
  margin: 0;
  background: ${tokens.color.white};
  padding: ${3.5 * tokens.size.base}px ${0.5 * tokens.size.base}px
    ${tokens.size.base}px ${3 * tokens.size.base}px;
  padding-bottom: ${tokens.size.base}px;
  padding-top: ${3.5 * tokens.size.base}px;
  border-bottom: 1px solid ${tokens.color.border};

  border: ${props =>
    props.isDragging ? `solid 1px ${tokens.color.primary}` : "none"};
`
const StyledChildNavSection = styled.ul`
  margin: 0;
  background: ${props =>
    props.isDraggingOver
      ? tokens.color.primaryTransparent
      : tokens.color.white};
  padding-left: 0;
  border: ${props =>
    props.isDragging ? `solid 1px ${tokens.color.primary}` : "none"};
`

const NavSection = ({ item, children, index, changeMenuItem, deleteItem }) => (
  <Draggable draggableId={item.url} index={index} key={item.url}>
    {(provided, snapshot) => (
      <>
        <StyledNavSection
          ref={provided.innerRef}
          {...provided.draggableProps}
          isDragging={snapshot.isDragging}
        >
          <NavItem
            isParent
            item={item}
            index={index}
            changeMenuItem={changeMenuItem}
            deleteItem={deleteItem}
            dragHandleProps={provided.dragHandleProps}
          />

          <Droppable
            droppableId={item.id}
            key={`NavItem__${item.id}`}
            type={`droppableChildItem`}
          >
            {(provided, snapshot) => (
              <>
                <StyledChildNavSection
                  ref={provided.innerRef}
                  isDraggingOver={snapshot.isDraggingOver}
                >
                  {children}
                </StyledChildNavSection>
                {provided.placeholder}
              </>
            )}
          </Droppable>
        </StyledNavSection>
      </>
    )}
  </Draggable>
)

NavSection.propTypes = {
  changeMenuItem: PropTypes.func,
  deleteItem: PropTypes.func,
  children: PropTypes.node,
  index: PropTypes.number,
  item: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    url: PropTypes.string,
  }),
}

const ButtonAddSection = styled(Button)`
  align-self: flex-end;
  border-radius: 50%;
  transform: translateX(50%);
  line-height: inherit;
  font-size: 0;
  padding: 0;
`

function NavItems(props) {
  const [items, setItems] = useState([])

  useEffect(() => {
    setItems(props.items)
  }, props.items)

  function updateItems(items) {
    setItems(items)
    parent.postMessage({ nav: items }, "*")
  }

  const changeMenuItem = e => {
    let newItems = items.slice()
    // Change 1st level
    let firstLevelItem = newItems.find(el => el.id === e.target.id)
    if (firstLevelItem) {
      firstLevelItem.title = e.target.value
    } else {
      // Change 2nd levels
      newItems = newItems.map(item => {
        item.items =
          item.items &&
          item.items.map(child => {
            if (child.url === e.target.id) child.title = e.target.value
            return child
          })
        return item
      })
    }
    updateItems(newItems)
  }

  const deleteItem = e => {
    let newItems = items.slice()
    newItems = newItems.reduce((res, item) => {
      if (item.items) {
        item.items = item.items.filter(s => {
          return s.url != e.target.id
        })
      }
      item.url != e.target.id && res.push(item)
      return res
    }, [])

    updateItems(newItems)
  }

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)

    return result
  }

  // from https://codesandbox.io/s/nested-dnd-across-parent-forked-seki3?file=/index.js:1438-3161
  const onDragEnd = result => {
    const { destination, source } = result
    if (!destination) return
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    )
      return

    const sourceIndex = result.source.index
    const destIndex = result.destination.index
    if (result.type === "droppableItem") {
      const reorderedItems = reorder(items, sourceIndex, destIndex)
      updateItems(reorderedItems)
    } else if (result.type === "droppableChildItem") {
      const itemSubItemMap = items.reduce((acc, item) => {
        acc[item.id] = item.items
        return acc
      }, {})
      const sourceParentId = result.source.droppableId
      const destParentId = result.destination.droppableId
      const sourceSubItems = itemSubItemMap[sourceParentId]
      const destSubItems = itemSubItemMap[destParentId]
      let newItems = [...items]
      /** In this case  child items are reOrdered inside same Parent */
      if (sourceParentId === destParentId) {
        const reorderedSubItems = reorder(
          sourceSubItems,
          sourceIndex,
          destIndex
        )
        newItems = newItems.map(item => {
          if (item.id === sourceParentId) {
            item.items = reorderedSubItems
          }
          return item
        })
        updateItems(newItems)
      } else {
        let newSourceSubItems = [...sourceSubItems]

        const [draggedItem] = newSourceSubItems.splice(sourceIndex, 1)
        let newDestSubItems = [...destSubItems]

        newDestSubItems.splice(destIndex, 0, draggedItem)

        newItems = newItems.map(item => {
          if (item.id === sourceParentId) {
            item.items = newSourceSubItems
          } else if (item.id === destParentId) {
            item.items = newDestSubItems
          }
          return item
        })

        updateItems(newItems)
      }
    }
  }

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <NavContainer>
          {items.map((item, index) => (
            <NavSection
              item={item}
              index={index}
              key={item.url}
              changeMenuItem={changeMenuItem}
              deleteItem={deleteItem.bind(this)}
            >
              {item.items &&
                item.items.map((child, index) => (
                  <NavItem
                    key={child.url + "navitem"}
                    index={index}
                    item={child}
                    changeMenuItem={changeMenuItem.bind(this)}
                    deleteItem={deleteItem.bind(this)}
                  />
                ))}
            </NavSection>
          ))}
        </NavContainer>
      </DragDropContext>
      <ButtonAddSection>
        <Icon name="plus" width="45" height="45" />
      </ButtonAddSection>
      {repositoryUrl ? (
        <StyledRepoUrlContainer>
          <Link href={repositoryUrl} style={{ color: "inherit" }}>
            GitHub
          </Link>
        </StyledRepoUrlContainer>
      ) : null}
    </>
  )
}

NavItems.propTypes = {
  items: PropTypes.array,
}

export default NavItems
